import { Controller, useWatch, type Control } from 'react-hook-form';

import { useLanguage } from '@/contexts/language-context';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import type { RecurrenceFormValues } from './schema';

interface Props {
  // Any job form that spreads `recurrenceFormFields` — add-job and job-detail.
  control: Control<RecurrenceFormValues>;
}

// Recurrence sub-form for the Add Job modal. Bound to the same RHF form via
// `control`; the parent maps `recurrence` → `JobRecurrence` on submit and
// ignores it entirely when `repeats` is false.
export function RecurrenceFields({ control }: Props) {
  const { t } = useLanguage();

  const repeats = useWatch({ control, name: 'repeats' });
  const frequency = useWatch({ control, name: 'recurrence.frequency' });

  return (
    <div className="flex flex-col gap-4 rounded-md border p-3">
      <Controller
        control={control}
        name="repeats"
        render={({ field }) => (
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
              onBlur={field.onBlur}
              ref={field.ref}
              data-testid="job-recurrence-toggle"
              className="size-4 rounded border"
            />
            {t.jobRepeats}
          </label>
        )}
      />

      {repeats && (
        <>
          <Controller
            control={control}
            name="recurrence.frequency"
            render={({ field }) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="job-frequency">{t.jobFrequency}</Label>
                <select
                  {...field}
                  id="job-frequency"
                  data-testid="job-frequency-select"
                  className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="weekly">{t.frequencyWeekly}</option>
                  <option value="monthly">{t.frequencyMonthly}</option>
                  <option value="custom">{t.frequencyCustom}</option>
                </select>
              </div>
            )}
          />

          <Controller
            control={control}
            name="recurrence.intervalCount"
            render={({ field }) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="job-interval">{t.jobRepeatEvery}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    {...field}
                    id="job-interval"
                    type="number"
                    min={1}
                    className="w-24"
                    data-testid="job-interval-input"
                  />
                  <span className="text-sm text-muted-foreground">
                    {frequency === 'monthly'
                      ? t.jobRepeatEveryMonths
                      : t.jobRepeatEveryWeeks}
                  </span>
                </div>
              </div>
            )}
          />

          {frequency === 'weekly' && (
            <Controller
              control={control}
              name="recurrence.daysOfWeek"
              render={({ field, fieldState }) => {
                const selected = field.value ?? [];
                return (
                  <div className="flex flex-col gap-1.5">
                    <Label>{t.jobDaysOfWeek}</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {t.weekdayShort.map((label, day) => {
                        const on = selected.includes(day);
                        return (
                          <button
                            key={label}
                            type="button"
                            data-testid={`job-weekday-${day}`}
                            aria-pressed={on}
                            onClick={() =>
                              field.onChange(
                                on
                                  ? selected.filter((d) => d !== day)
                                  : [...selected, day].sort((a, b) => a - b),
                              )
                            }
                            className={[
                              'px-2.5 py-1 text-xs rounded-md border transition-colors',
                              on
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-background text-muted-foreground hover:text-foreground',
                            ].join(' ')}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    {fieldState.error && (
                      <p className="text-xs text-destructive">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                );
              }}
            />
          )}

          {frequency === 'monthly' && (
            <Controller
              control={control}
              name="recurrence.dayOfMonth"
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="job-day-of-month">{t.jobDayOfMonth}</Label>
                  <Input
                    {...field}
                    id="job-day-of-month"
                    type="number"
                    min={1}
                    max={31}
                    className="w-24"
                    aria-invalid={fieldState.invalid}
                    data-testid="job-day-of-month-input"
                  />
                  {fieldState.error && (
                    <p className="text-xs text-destructive">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />
          )}

          {frequency === 'custom' && (
            <Controller
              control={control}
              name="recurrence.customRule"
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="job-custom-rule">{t.jobCustomRule}</Label>
                  <Input
                    {...field}
                    id="job-custom-rule"
                    placeholder={t.jobCustomRulePlaceholder}
                    aria-invalid={fieldState.invalid}
                    data-testid="job-custom-rule-input"
                  />
                  {fieldState.error && (
                    <p className="text-xs text-destructive">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />
          )}

          <Controller
            control={control}
            name="recurrence.seriesEndDate"
            render={({ field }) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="job-series-end">{t.jobSeriesEndDate}</Label>
                <Input
                  {...field}
                  id="job-series-end"
                  type="date"
                  className="w-fit"
                  data-testid="job-series-end-input"
                />
              </div>
            )}
          />
        </>
      )}
    </div>
  );
}
